import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  TextField,
  Typography,
  Link as MuiLink,
} from '@mui/material'
import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { postRequestPasswordReset } from 'src/api/authentication'

interface ForgotPasswordPageProps {
  theme: any
}

const ForgotPasswordPage = ({ theme }: ForgotPasswordPageProps) => {
  const [emailAddress, setEmailAddress] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!emailAddress) return

    setIsSubmitting(true)
    setError(null)

    try {
      await postRequestPasswordReset(emailAddress)
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'An error occurred while requesting a password reset')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        flex:   1,
        height: '100%',
      }}
    >
      <Container maxWidth="sm" sx={{ pt: 15 }}>
        <Card sx={{ p: 5 }}>
          <Typography
            variant="h1"
            color="text.primary"
            component="h1"
            gutterBottom
            fontWeight={500}
          >
            Forgot Password
          </Typography>
          <Typography
            variant="h4"
            component="div"
            color="text.secondary"
            fontSize={20}
            fontWeight={300}
            sx={{ mb: 3 }}
          >
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {success ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                If your email address exists in our database, you will receive a password recovery link shortly.
              </Alert>
              <Box sx={{ mt: 3 }}>
                <MuiLink component={Link} to="/" underline="hover">
                  Return to login
                </MuiLink>
              </Box>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                required
                id="email"
                label="Email Address"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                fullWidth
                sx={{ mt: 3, mb: 2 }}
              />

              {error && (
                <Alert color="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {isSubmitting ? (
                <Box textAlign="center">
                  <CircularProgress/>
                </Box>
              ) : (
                <Button
                  type="submit"
                  size="large"
                  color="primary"
                  variant="contained"
                  fullWidth
                  disabled={!emailAddress}
                  sx={{ mt: 3, height: '60px' }}
                >
                  Send Reset Link
                </Button>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <MuiLink component={Link} to="/" underline="hover">
                  Return to login
                </MuiLink>
              </Box>
            </Box>
          )}
        </Card>
      </Container>
    </Box>
  )
}

export default ForgotPasswordPage 